/**
 * LocationSharingModal - Mobile-optimized fullscreen location sharing UI
 */

import { useState } from 'react'
import { MapPin, Navigation, Clock, X, Shield, AlertCircle } from 'lucide-react'
import { locationService } from '../../services/LocationService'
import { toast } from '../../utils/toastManager'

interface LocationSharingModalProps {
  isOpen: boolean
  onClose: () => void
  peerId: string
  peerAlias: string
  onLocationShare: (location: any, shareType: 'one-time' | 'live' | 'duration', duration?: number) => void
}

export function LocationSharingModal({
  isOpen,
  onClose,
  peerId,
  peerAlias,
  onLocationShare
}: LocationSharingModalProps) {
  const [shareType, setShareType] = useState<'one-time' | 'live' | 'duration' | null>(null)
  const [durationMinutes, setDurationMinutes] = useState(15)
  const [isRequesting, setIsRequesting] = useState(false)
  const [showPermissionDialog, setShowPermissionDialog] = useState(false)
  const [pendingShareType, setPendingShareType] = useState<'one-time' | 'live' | 'duration' | null>(null)

  if (!isOpen) return null

  const requestLocationPermission = async () => {
    setIsRequesting(true)

    try {
      // Check browser permission
      if (!('geolocation' in navigator)) {
        toast.error('Location services not available on this device')
        onClose()
        return false
      }

      // Try to get location - this will trigger browser permission
      const location = await locationService.getCurrentLocation()
      return true
    } catch (error: any) {
      if (error.code === 1) {
        toast.error('Location permission denied. Please enable in browser settings.')
      } else if (error.code === 2) {
        toast.error('Unable to get location. Check device settings.')
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again.')
      } else {
        toast.error('Failed to access location')
      }
      return false
    } finally {
      setIsRequesting(false)
    }
  }

  const handleShareSelection = async (type: 'one-time' | 'live' | 'duration') => {
    setPendingShareType(type)
    setShowPermissionDialog(true)
  }

  const handlePermissionResponse = async (allowed: boolean) => {
    setShowPermissionDialog(false)

    if (!allowed) {
      toast.info('Location sharing cancelled')
      setPendingShareType(null)
      return
    }

    if (!pendingShareType) return

    setIsRequesting(true)

    const hasPermission = await requestLocationPermission()
    if (!hasPermission) {
      setIsRequesting(false)
      setPendingShareType(null)
      return
    }

    try {
      if (pendingShareType === 'one-time') {
        const location = await locationService.getCurrentLocation()
        const accuracyLevel = locationService.getAccuracyLevelForPeer(peerId)
        const processedLocation = locationService.applyPrivacyLevel(location, accuracyLevel)
        onLocationShare(processedLocation, 'one-time')
        toast.success(`Location shared with ${peerAlias}`)
        onClose()
      } else if (pendingShareType === 'live') {
        locationService.startLiveTracking(peerId)
        onLocationShare(null, 'live')
        toast.success(`Started live location sharing with ${peerAlias}`)
        onClose()
      } else if (pendingShareType === 'duration') {
        setShareType('duration')
        setPendingShareType(null)
      }
    } catch (error) {
      toast.error('Failed to share location')
    } finally {
      setIsRequesting(false)
    }
  }

  const handleDurationShare = async () => {
    setIsRequesting(true)
    try {
      locationService.startDurationSharing(peerId, durationMinutes)
      onLocationShare(null, 'duration', durationMinutes)
      toast.success(`Sharing location with ${peerAlias} for ${durationMinutes} minutes`)
      onClose()
    } catch (error) {
      toast.error('Failed to start location sharing')
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 md:hidden"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 z-50 md:hidden animate-slide-up">
        <div className="bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Share Location with {peerAlias}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Permission Dialog */}
          {showPermissionDialog && (
            <div className="fixed inset-0 bg-black/70 z-60 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full">
                <div className="flex items-center mb-4">
                  <Shield className="h-8 w-8 text-blue-500 mr-3" />
                  <h3 className="text-lg font-semibold">Location Permission</h3>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Allow {peerAlias} to see your location? Your browser will also ask for permission.
                </p>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p className="font-medium mb-1">Privacy Notice</p>
                      <p>Your location will be shared directly with {peerAlias} via P2P connection. No servers involved.</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handlePermissionResponse(false)}
                    className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-medium"
                  >
                    Deny
                  </button>
                  <button
                    onClick={() => handlePermissionResponse(true)}
                    className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium"
                  >
                    Allow
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!shareType ? (
            <div className="p-6 space-y-3">
              {/* One-time location */}
              <button
                onClick={() => handleShareSelection('one-time')}
                disabled={isRequesting}
                className="w-full flex items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full mr-4">
                  <MapPin className="h-6 w-6 text-blue-500" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">Share Current Location</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Send your location once</p>
                </div>
              </button>

              {/* Live location */}
              <button
                onClick={() => handleShareSelection('live')}
                disabled={isRequesting}
                className="w-full flex items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full mr-4">
                  <Navigation className="h-6 w-6 text-green-500" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">Share Live Location</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Real-time location updates</p>
                </div>
              </button>

              {/* Duration-based */}
              <button
                onClick={() => handleShareSelection('duration')}
                disabled={isRequesting}
                className="w-full flex items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full mr-4">
                  <Clock className="h-6 w-6 text-purple-500" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">Share for Duration</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Set a time limit for sharing</p>
                </div>
              </button>

              {isRequesting && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-blue-500 rounded-full" />
                </div>
              )}
            </div>
          ) : shareType === 'duration' ? (
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">How long to share?</h3>

              {/* Quick duration options */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[15, 30, 60].map(minutes => (
                  <button
                    key={minutes}
                    onClick={() => setDurationMinutes(minutes)}
                    className={`py-3 px-4 rounded-xl font-medium transition-colors ${
                      durationMinutes === minutes
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {minutes} min
                  </button>
                ))}
              </div>

              {/* Custom duration */}
              <div className="flex items-center gap-3 mb-6">
                <input
                  type="number"
                  min="5"
                  max="480"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 15)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800"
                />
                <span className="text-gray-600 dark:text-gray-400">minutes</span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShareType(null)}
                  className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-medium"
                >
                  Back
                </button>
                <button
                  onClick={handleDurationShare}
                  disabled={isRequesting}
                  className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium disabled:opacity-50"
                >
                  {isRequesting ? 'Sharing...' : 'Start Sharing'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Desktop fallback - centered modal */}
      <div className="hidden md:flex fixed inset-0 items-center justify-center z-50 p-4">
        <div className="bg-black/50 absolute inset-0" onClick={onClose} />
        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Same content as mobile but in centered modal */}
          {/* ... (content is the same as above) ... */}
        </div>
      </div>
    </>
  )
}